import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Download, RotateCcw, Trash2, Copy } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useLiveUpdateStore } from '@/features/liveUpdate/liveUpdateStore'
import {
  applyPendingUpdate,
  forceCheck,
  resetToNative,
} from '@/core/platform/liveUpdate'
import { isNative, getPlatform } from '@/core/platform/runtime'
import { showToast } from '@/features/common/Toast'

/**
 * Tela de diagnóstico in-app pro Live Update. Acessada via tap longo no
 * v0.X.Y do footer (home.tsx). Mostra estado completo + log + botões de
 * recuperação. Pensada pra debug remoto: operador em campo abre, tira print,
 * envia pro suporte.
 */
export function AdminLiveUpdateRoute() {
  const navigate = useNavigate()
  const state = useLiveUpdateStore()
  const [manifestRaw, setManifestRaw] = useState<string>('(carregando…)')
  const [manifestError, setManifestError] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    void loadManifest()
  }, [])

  async function loadManifest() {
    setManifestRaw('(carregando…)')
    setManifestError(null)
    try {
      const r = await fetch('https://infrawork.pages.dev/api/live-update-debug', {
        cache: 'no-store',
      })
      const text = await r.text()
      setManifestRaw(text)
    } catch (e) {
      setManifestError(e instanceof Error ? e.message : String(e))
      setManifestRaw('(erro)')
    }
  }

  async function handleCheck() {
    setChecking(true)
    try {
      await forceCheck()
    } finally {
      setChecking(false)
      await loadManifest()
    }
  }

  async function handleApply() {
    await applyPendingUpdate()
  }

  async function handleReset() {
    const ok = window.confirm(
      'Resetar pro APK nativo? Isso descarta a versão atual em uso e volta pro bundle do instalador. Use só pra recuperar de bundle ruim.',
    )
    if (!ok) return
    await resetToNative()
  }

  function handleClearLog() {
    state.clearLog()
  }

  async function handleCopy() {
    const text = buildDiagnostic(state, manifestRaw)
    try {
      await navigator.clipboard.writeText(text)
      showToast('ok', 'Diagnóstico copiado pro clipboard.')
    } catch {
      showToast('bad', 'Não foi possível copiar — selecione manualmente.')
    }
  }

  return (
    <main className="min-h-svh bg-bg-base text-on-surface">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Voltar"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-bg-elevated"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-base font-semibold">Live Update — Diagnóstico</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        <Section title="Ambiente">
          <Row label="Plataforma" value={getPlatform()} />
          <Row label="É nativo" value={isNative() ? 'Sim (APK)' : 'Não (PWA)'} />
        </Section>

        <Section title="Status do bundle">
          <Row
            label="Bundle ativo"
            value={state.currentVersion ?? '(desconhecido — verifique abaixo)'}
            mono
          />
          <Row
            label="Última verificação"
            value={
              state.lastCheckAt
                ? new Date(state.lastCheckAt).toLocaleString('pt-BR')
                : '(nunca)'
            }
          />
          <Row label="Resultado" value={resultLabel(state.lastCheckResult)} />
          {state.lastError && (
            <Row label="Último erro" value={state.lastError} danger />
          )}
          <Row
            label="Update pendente"
            value={
              state.pendingUpdate
                ? `${state.pendingUpdate.version} (id=${state.pendingUpdate.id})`
                : 'nenhum'
            }
            mono={!!state.pendingUpdate}
          />
        </Section>

        <Section title="Manifest remoto (R2)">
          {manifestError && (
            <p className="text-bad text-xs mb-2">erro: {manifestError}</p>
          )}
          <pre className="text-xs bg-bg-elevated rounded-md p-3 overflow-x-auto font-mono whitespace-pre-wrap break-all">
            {manifestRaw}
          </pre>
        </Section>

        <Section title="Ações">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCheck}
              disabled={checking}
              className="!justify-start"
            >
              <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
              <span>{checking ? 'Verificando…' : 'Verificar agora'}</span>
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleApply}
              disabled={!state.pendingUpdate}
              className="!justify-start"
            >
              <Download className="h-4 w-4" />
              <span>Aplicar agora</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="!justify-start"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Resetar pro APK</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="!justify-start"
            >
              <Copy className="h-4 w-4" />
              <span>Copiar tudo</span>
            </Button>
          </div>
        </Section>

        <Section
          title={`Log de eventos (${state.eventLog.length})`}
          right={
            state.eventLog.length > 0 && (
              <button
                onClick={handleClearLog}
                className="text-xs text-on-surface-dim hover:text-on-surface inline-flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                limpar
              </button>
            )
          }
        >
          {state.eventLog.length === 0 ? (
            <p className="text-sm text-on-surface-muted">
              Sem eventos. Toque em "Verificar agora" pra disparar um check.
            </p>
          ) : (
            <ul className="text-xs font-mono space-y-1.5 max-h-96 overflow-y-auto">
              {state.eventLog.map((ev) => (
                <li
                  key={ev.ts}
                  className={`flex gap-2 ${
                    ev.level === 'warn' ? 'text-bad' : 'text-on-surface-dim'
                  }`}
                >
                  <span className="opacity-60 shrink-0">
                    {new Date(ev.ts).toLocaleTimeString('pt-BR')}
                  </span>
                  <span className="shrink-0">[{ev.level}]</span>
                  <span className="break-all">{ev.msg}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </main>
  )
}

function Section({
  title,
  right,
  children,
}: {
  title: string
  right?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="bg-bg-panel border border-border rounded-lg overflow-hidden">
      <header className="px-4 py-2.5 border-b border-border bg-bg-elevated/30 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-on-surface-dim">
          {title}
        </h2>
        {right}
      </header>
      <div className="p-4 space-y-2">{children}</div>
    </section>
  )
}

function Row({
  label,
  value,
  mono = false,
  danger = false,
}: {
  label: string
  value: string
  mono?: boolean
  danger?: boolean
}) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-on-surface-dim shrink-0 w-32">{label}</span>
      <span
        className={`flex-1 break-all ${mono ? 'font-mono text-xs' : ''} ${
          danger ? 'text-bad' : ''
        }`}
      >
        {value}
      </span>
    </div>
  )
}

function resultLabel(r: ReturnType<typeof useLiveUpdateStore.getState>['lastCheckResult']) {
  switch (r) {
    case 'idle':
      return 'aguardando primeiro check'
    case 'up_to_date':
      return '✓ atualizado'
    case 'newer':
      return '↓ versão nova disponível'
    case 'error':
      return '✗ erro'
  }
}

function buildDiagnostic(
  state: ReturnType<typeof useLiveUpdateStore.getState>,
  manifest: string,
): string {
  const lines = [
    `# Live Update — Diagnóstico`,
    `Gerado: ${new Date().toLocaleString('pt-BR')}`,
    `Plataforma: ${getPlatform()}`,
    `Nativo: ${isNative() ? 'sim' : 'não'}`,
    ``,
    `## Estado`,
    `Bundle ativo: ${state.currentVersion ?? '(desconhecido)'}`,
    `Última verificação: ${
      state.lastCheckAt ? new Date(state.lastCheckAt).toLocaleString('pt-BR') : '(nunca)'
    }`,
    `Resultado: ${state.lastCheckResult}`,
    `Erro: ${state.lastError ?? '(nenhum)'}`,
    `Pendente: ${
      state.pendingUpdate
        ? `${state.pendingUpdate.version} (id=${state.pendingUpdate.id})`
        : '(nenhum)'
    }`,
    ``,
    `## Manifest remoto`,
    manifest,
    ``,
    `## Log (${state.eventLog.length})`,
    ...state.eventLog.map(
      (e) =>
        `${new Date(e.ts).toLocaleTimeString('pt-BR')} [${e.level}] ${e.msg}`,
    ),
  ]
  return lines.join('\n')
}
